import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowLeft, Bookmark, BookmarkCheck, Share2, Building2, MapPin, BadgeCheck, Lock, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { shareJob } from "@/lib/share";
import HighlightedDescription from "@/components/HighlightedDescription";
import { useSEO } from "@/lib/useSEO";
import AdSlot from "@/components/AdSlot";
import { getLocationBadge } from "@/lib/countryBadge";

const EMPLOYMENT_TYPE_MAP = {
  "full-time": "FULL_TIME",
  "part-time": "PART_TIME",
  contract: "CONTRACTOR",
  contractor: "CONTRACTOR",
  temporary: "TEMPORARY",
  intern: "INTERN",
  internship: "INTERN",
};

export default function JobDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setJob(null);
    setFetchFailed(false);
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setFetchFailed(true));
    if (user) {
      api.get("/saved-jobs/me").then((r) => {
        setSaved(!!(r.data.items || []).find((i) => i.job_id === id));
      });
    }
  }, [id, user]);

  useSEO({
    title: job ? `${job.title} at ${job.company} — KARM` : "Loading role — KARM",
    description: job ? `${job.title} at ${job.company}, ${job.location}. Apply directly, no recruiters — via KARM.` : undefined,
    path: `/jobs/${id}`,
    // Ask Google to drop a closed role from the index instead of leaving a
    // stale/expired page indexed — the corresponding <script type="ld+json">
    // JobPosting markup below is likewise skipped for inactive jobs.
    noindex: job ? job.is_active === false : false,
  });

  const trackAndRedirect = async () => {
    try {
      const r = await api.post("/applications", { job_id: id });
      toast.success("Application tracked. Opening company site…");
      const url = r.data.redirect_url || job.apply_url;
      if (url) setTimeout(() => window.open(url, "_blank"), 400);
    } catch {
      toast.error("Could not track application");
    }
  };

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save."); return; }
    try {
      if (saved) { await api.delete(`/saved-jobs/${id}`); setSaved(false); }
      else { await api.post(`/saved-jobs/${id}`); setSaved(true); }
    } catch {
      toast.error("Couldn't update saved jobs — please try again.");
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (fetchFailed) {
    return (
      <div className="container py-20 text-center" data-testid="job-detail-error">
        <p className="text-muted-foreground mb-4">We couldn't load this role — it may have been removed, or the link is broken.</p>
        <Link to="/jobs" className="text-primary font-semibold hover:underline" data-testid="job-detail-error-back">
          Back to all jobs
        </Link>
      </div>
    );
  }
  if (!job) return <div className="container py-20 text-muted-foreground">Loading…</div>;

  const locked = job.locked;
  const badge = getLocationBadge(job);

  const COUNTRY_TO_ISO = {
    "India": "IN", "United States": "US", "United Kingdom": "GB",
    "Singapore": "SG", "Australia": "AU",
  };
  const isoCountry = COUNTRY_TO_ISO[job.country];

  const jobPostingSchema = job.is_active === false ? null : {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    title: job.title,
    description: job.description || job.title,
    identifier: { "@type": "PropertyValue", name: job.company, value: job.job_id },
    datePosted: job.posted_at,
    hiringOrganization: { "@type": "Organization", name: job.company },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.location,
        ...(isoCountry && { addressCountry: isoCountry }),
      },
    },
    directApply: true,
    ...(EMPLOYMENT_TYPE_MAP[(job.type || "").toLowerCase()] && {
      employmentType: EMPLOYMENT_TYPE_MAP[job.type.toLowerCase()],
    }),
    ...(!locked && job.salary_max && {
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: job.currency || "USD",
        value: { "@type": "QuantitativeValue", minValue: job.salary_min, maxValue: job.salary_max, unitText: "YEAR" },
      },
    }),
  };

  return (
    <div className="container py-10 max-w-4xl">
      {jobPostingSchema && (
        <script type="application/ld+json">{JSON.stringify(jobPostingSchema)}</script>
      )}
      <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-jobs">
        <ArrowLeft className="h-4 w-4" /> Back to all jobs
      </Link>

      <div className="vital-card-static p-8 md:p-10">
        <div className="flex items-start gap-4 mb-6">
          <div className="h-14 w-14 rounded-md bg-muted border border-border flex items-center justify-center">
            <Building2 className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <p className="eyebrow mb-1">{job.company}</p>
            <h1 className="display-2 text-2xl md:text-3xl mb-2" data-testid="job-detail-title">{job.title}</h1>
            <div className="flex flex-wrap gap-2 text-sm">
              {badge && (
                <span className="pill" title={badge.label} data-testid="job-detail-country-badge">
                  <span aria-hidden="true">{badge.flag}</span> {badge.label}
                </span>
              )}
              <span className="pill"><MapPin className="h-3 w-3 mr-1" />{job.location}</span>
              <span className="pill">{job.type}</span>
              {job.level && <span className="pill">{job.level}</span>}
              {!locked && job.salary_max && <span className="pill text-primary font-bold">₹{(job.salary_min / 100000).toFixed(0)}L – ₹{(job.salary_max / 100000).toFixed(0)}L</span>}
              <span className="pill"><BadgeCheck className="h-3 w-3 mr-1 text-primary" />Direct from {job.company}</span>
            </div>
          </div>
        </div>

        {job.skills?.length > 0 && (
          <div className="mb-6">
            <p className="eyebrow mb-2">Skills</p>
            <div className="flex flex-wrap gap-1.5">
              {job.skills.map((s) => <span key={s} className="text-xs px-2 py-1 rounded bg-muted">{s}</span>)}
            </div>
          </div>
        )}

        {job.is_active === false && (
          <div
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-6 text-sm text-destructive font-medium"
            data-testid="job-inactive-banner"
          >
            This role is no longer active — it's been removed from {job.company}&apos;s careers site and is no
            longer accepting applications.
          </div>
        )}

        {locked ? (
          <div className="rounded-xl border border-border bg-muted/30 p-6 mb-8" data-testid="locked-cta-block">
            <p className="text-base leading-relaxed mb-4 text-foreground/80">
              {job.description}
              <span className="text-muted-foreground"> </span>
            </p>
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Lock className="h-6 w-6 text-primary shrink-0" />
              <div className="flex-1">
                <p className="font-bold">Full description, salary range & apply link — for members.</p>
                <p className="text-sm text-muted-foreground">Free account · 10-second signup · no credit card.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <Button asChild size="lg" data-testid="unlock-signup-btn">
                  <Link to="/auth">Sign up free</Link>
                </Button>
                <Button onClick={handleGoogle} variant="outline" size="lg" data-testid="unlock-google-btn">
                  <Sparkles className="mr-2 h-4 w-4" /> Continue with Google
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center mt-3">
              Already have an account? <Link to="/auth" className="underline font-medium" data-testid="unlock-signin-link">Sign in</Link>
            </p>
          </div>
        ) : (
          <HighlightedDescription text={job.description} />
        )}

        {!locked && (
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={toggleSave} variant="outline" size="lg" data-testid="save-toggle-btn">
              {saved ? <><BookmarkCheck className="mr-2 h-4 w-4 text-primary" />Saved</> : <><Bookmark className="mr-2 h-4 w-4" />Save role</>}
            </Button>
            <Button onClick={() => shareJob(job)} variant="outline" size="lg" data-testid="share-job-btn">
              <Share2 className="mr-2 h-4 w-4" />Share
            </Button>
            {job.apply_url && (
              <Button asChild={job.is_active !== false} disabled={job.is_active === false} size="lg" data-testid="apply-direct-btn">
                {job.is_active === false ? (
                  <span>No longer accepting applications</span>
                ) : (
                  <a href={job.apply_url} target="_blank" rel="noreferrer" onClick={trackAndRedirect}>
                    Apply on company site <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Google AdSense — renders only once REACT_APP_GOOGLE_ADSENSE_JOBS_SLOT is set
          (create an "in-article" ad unit in AdSense and drop its slot ID into that env var).
          Mounted on every job-detail page, including crawler-sourced roles. */}
      <AdSlot slot={process.env.REACT_APP_GOOGLE_ADSENSE_JOBS_SLOT} format="fluid" className="mt-8" />
    </div>
  );
}
